import { Circle, Fill, Stroke, Style, Text } from "@vision/core-map-api";
import VectorLayerStyles from "@vision/core-map-api/src/utils/VectorLayerStyles";
import type { Viewerlayer } from "../../../viewer-components/model/viewerlayer";
import { convertVectorStyle } from "../../../shared/utils/geojson-style-converter";

/**
 * Static OL style functions and helpers for AI-added layers (pipeline outputs, places hints).
 * Keeps style logic out of BaseViewerService and co-located with the AI chat module that owns these layers.
 */
export class AiLayerStyles {
  static readonly PIPELINE_LAYER_STYLES: Readonly<Record<string, { colorFill: string; colorOutline: string }>> = {
    damage: { colorFill: "rgba(220,57,18,0.3)", colorOutline: "rgba(220,57,18,1)" },
    vlm: { colorFill: "rgba(153,0,153,0.07)", colorOutline: "rgba(0,0,0,0)" },
  };

  /**
   * Reads a backend-provided style definition off a raw GeoJSON collection (`.style.style` and
   * `.style.metadata`, per the Style Agent wire contract) and attaches it to the layer as serializable
   * descriptors, so they survive IndexedDB persistence the same way styleFunctionName does. No-op for
   * whichever half (definition/metadata) the collection doesn't carry.
   */
  static attachStyleDefinition(layer: Viewerlayer, collection: any): void {
    const styleDefinition = collection?.style?.style;
    if (styleDefinition) {
      layer.styleDefinition = styleDefinition;
    }
    const styleMetadata = collection?.style?.metadata;
    if (styleMetadata) {
      layer.styleMetadata = styleMetadata;
    }
  }

  /** Resolves serializable styleFunctionName/styleDefinition descriptors to live OL style functions on the layer tree. */
  static applyStyleFunctions(layers: Viewerlayer[]): void {
    for (const layer of layers) {
      if (layer.childs?.length) {
        AiLayerStyles.applyStyleFunctions(layer.childs);
      }
      if (layer.styleDefinition) {
        const resolved = convertVectorStyle(layer.styleDefinition);
        if (resolved) layer.styleFunction = resolved;
      } else if (layer.styleFunctionName === "defaultPropertyFootprintStyle") {
        layer.styleFunction = VectorLayerStyles.defaultPropertyFootprintStyle();
      } else if (layer.styleFunctionName === "propertyPipelineStyle") {
        layer.styleFunction = AiLayerStyles.propertyPipelineStyleFn;
      } else if (layer.styleFunctionName === "embeddingsPipelineStyle") {
        layer.styleFunction = AiLayerStyles.embeddingsPipelineStyleFn;
      } else if (layer.styleFunctionName === "geofencePipelineStyle") {
        layer.styleFunction = AiLayerStyles.geofencePipelineStyleFn;
      } else if (layer.styleFunctionName === "aoiPipelineStyle") {
        layer.styleFunction = AiLayerStyles.aoiPipelineStyleFn;
      } else if (layer.styleFunctionName === "mergedPipelineStyle") {
        layer.styleFunction = AiLayerStyles.mergedPipelineStyleFn;
      } else if (layer.styleFunctionName === "placesStyle") {
        layer.styleFunction = AiLayerStyles.placesStyleFn;
      } else if (layer.styleFunctionName === "defaultEvidenceStyle") {
        layer.styleFunction = AiLayerStyles.defaultEvidenceStyleFn;
      } else if (layer.styleFunctionName === "evidenceWorkareaStyle") {
        layer.styleFunction = AiLayerStyles.workareaStyleFn;
      } else if (layer.styleFunctionName === "evidenceCollectionsStyle") {
        layer.styleFunction = AiLayerStyles.collectionsStyleFn;
      }
    }
  }

  /** OL style function for pipeline properties layers — parcel gets blue outline, structures get roof-score color + index label. */
  static propertyPipelineStyleFn(feature: any): any {
    if (feature.get("_footprint_type") === "structure") {
      const color: string = feature.get("_roof_color") ?? "#9e9e9e";
      const structureIndex: number | undefined = feature.get("_structure_index");
      const styles: any[] = [VectorLayerStyles.buildingPropertyFootprintStyle({ color })];
      if (structureIndex !== undefined && structureIndex !== null) {
        styles.push(VectorLayerStyles.buildingLabelStyle({ label: String(structureIndex) }));
      }
      return styles;
    }
    return VectorLayerStyles.defaultPropertyFootprintStyle();
  }

  /** OL style function for pipeline embeddings layers — colors features by scoreColor, zoom-dependent polygon visibility. */
  static embeddingsPipelineStyleFn(feature: any, resolution: number): any[] {
    const color: string = feature.get("scoreColor") ?? "rgba(0,150,136,0.8)";
    const score: number = feature.get("score") ?? 0.5;
    const geom = feature.getGeometry?.();
    if (!geom) return [];
    const type: string = geom.getType?.() ?? "";
    if (type === "Polygon" || type === "MultiPolygon") {
      if (resolution > 70) return [];
      return [new Style({ stroke: new Stroke({ color, width: 2 }), zIndex: score })];
    }
    if (type === "Point") {
      return [new Style({ image: new Circle({ radius: 4, fill: new Fill({ color }) }), zIndex: score })];
    }
    return [];
  }

  /** OL style function for pipeline geofence layers — dashed blue-grey polygon; point/line fallback for non-polygon geometries. */
  static geofencePipelineStyleFn(feature: any): any[] {
    const geom = feature.getGeometry?.();
    if (!geom) return [];
    const type: string = geom.getType?.() ?? "";
    if (type === "Polygon" || type === "MultiPolygon") {
      return [
        new Style({
          stroke: new Stroke({ color: "rgba(100,140,200,0.9)", width: 2, lineDash: [6, 4] }),
          fill: new Fill({ color: "rgba(100,140,200,0.04)" }),
        }),
      ];
    }
    if (type === "LineString" || type === "MultiLineString") {
      return [new Style({ stroke: new Stroke({ color: "rgba(100,140,200,0.9)", width: 2, lineDash: [6, 4] }) })];
    }
    // Point / MultiPoint fallback
    return [new Style({ image: new Circle({ radius: 5, fill: new Fill({ color: "rgba(100,140,200,0.9)" }), stroke: new Stroke({ color: "rgba(255,255,255,0.9)", width: 1 }) }) })];
  }

  /** OL style function for pipeline AOI layers — zone gets semitransparent fill, POIs get labeled circle pins. */
  static aoiPipelineStyleFn(feature: any): any[] {
    const type: string = feature.get("type");
    if (type === "zone") {
      return [
        new Style({
          stroke: new Stroke({ color: "rgba(0,180,160,0.6)", width: 1.5 }),
          fill: new Fill({ color: "rgba(0,180,160,0.12)" }),
        }),
      ];
    }
    if (type === "poi") {
      const name: string = feature.get("name") ?? "";
      const styles: any[] = [
        new Style({ image: new Circle({ radius: 6, fill: new Fill({ color: "rgba(0,130,120,0.9)" }) }) }),
      ];
      if (name) {
        styles.push(
          new Style({
            text: new Text({
              text: name,
              font: "11px sans-serif",
              offsetY: -16,
              fill: new Fill({ color: "#ffffff" }),
              stroke: new Stroke({ color: "rgba(0,0,0,0.7)", width: 2 }),
            }),
          }),
        );
      }
      return styles;
    }
    return [];
  }

  /** OL style function for pipeline merged layers — filtered features (grey) vs analyzed (blue-grey). */
  static mergedPipelineStyleFn(feature: any): any[] {
    const stage: string = feature.get("_stage");
    if (stage === "filtered") {
      return [
        new Style({
          stroke: new Stroke({ color: "rgba(150,150,150,0.6)", width: 1 }),
          fill: new Fill({ color: "rgba(150,150,150,0.15)" }),
        }),
      ];
    }
    return [
      new Style({
        stroke: new Stroke({ color: "rgba(69,90,100,1)", width: 1.5 }),
        fill: new Fill({ color: "rgba(69,90,100,0.3)" }),
      }),
    ];
  }

  /** OL style function for places hint layers — points get a small circle, polygons get a white dashed zone outline. */
  static placesStyleFn(feature: any): any[] {
    const geom = feature.getGeometry?.();
    if (!geom) return [];
    const type: string = geom.getType?.() ?? "";
    if (type === "Point") {
      return [
        new Style({
          image: new Circle({
            radius: 5,
            fill: new Fill({ color: "rgba(66,133,244,0.9)" }),
            stroke: new Stroke({ color: "rgba(255,255,255,0.9)", width: 1 }),
          }),
        }),
      ];
    }
    if (type === "Polygon" || type === "MultiPolygon") {
      return [
        new Style({
          stroke: new Stroke({ color: "rgba(255,255,255,0.8)", width: 1.5, lineDash: [6, 4] }),
          fill: new Fill({ color: "rgba(255,255,255,0.06)" }),
        }),
      ];
    }
    return [];
  }

  /**
   * OL style function for the evidence-layer contract's `defaultEvidenceStyle` (evidence-layer-ui
   * T2) — neutral, geometry-typed fallback for every `LayerType` without a dedicated pipeline
   * style function (see `LAYER_TYPE_TO_STYLE_FN`): points get a dark marker, linestrings a grey
   * stroke, polygons a slate fill+outline.
   */
  /**
   * Deliberately VISIBLE colors for evidence layers with no type-specific style (defect
   * round: neutral grays read as "no style applied" on any basemap). Exported constants so
   * tests can pin the colors without constructing real OL style objects.
   */
  static readonly DEFAULT_EVIDENCE_COLORS = {
    polygonStroke: "#cbd5e1",
    polygonFill: "rgba(148,163,184,0.08)",
    lineStroke: "#94a3b8",
    lineStrokeWidth: 2,
    pointFill: "#64748b",
  } as const;

  /** Workarea style colors — a translucent gray veil (fill-only, no stroke) per user brief. */
  static readonly EVIDENCE_WORKAREA_COLORS = {
    fill: "rgba(100,116,139,0.10)",
    lineStroke: "rgba(100,116,139,0.35)",
    lineStrokeWidth: 2,
    pointFill: "rgba(100,116,139,0.25)",
  } as const;

  /** Collections style colors — near-invisible fill over imagery with a white standing-out
   *  border: collection footprints overlap, so the stroke is what delineates them. */
  static readonly EVIDENCE_COLLECTIONS_COLORS = {
    stroke: "rgba(255,255,255,0.9)",
    strokeWidth: 1.75,
    fill: "rgba(99,102,241,0.05)",
    labelColor: "#374151",
    labelHalo: "rgba(255,255,255,0.9)",
    labelFont: "11px Roboto",
  } as const;

  /** Collections labels render only when zoomed in — OL resolution at or below this value. */
  static readonly COLLECTIONS_LABEL_MAX_RESOLUTION = 20;

  /**
   * Declarative `IVectorStyle` definitions (the ol-styler / advanced-editor wire format) for the
   * evidence styles, keyed by their registered `styleFunctionName`. Attached to evidence layers at
   * add time (unless the collection carries its own Style Agent definition) so the advanced style
   * editor can read and edit them; `applyStyleFunctions` prefers `styleDefinition` over the named
   * function, which stays as fallback. Keys use the exact OL flat conventions the editor writes
   * (`fill-color`, `stroke-*`, `text-*`), and the label rule gates on `["resolution"]`, which the
   * canvas style evaluator provides (unlike `["zoom"]`, which is WebGL-only).
   */
  static readonly EVIDENCE_STYLE_DEFINITIONS: Record<string, any> = {
    evidenceWorkareaStyle: {
      type: "flat",
      properties: {
        "fill-color": AiLayerStyles.EVIDENCE_WORKAREA_COLORS.fill,
      },
    },
    evidenceCollectionsStyle: {
      type: "rules",
      rules: [
        {
          // Base symbolizer: thin border + soft fill (circle keys only affect point geometries).
          style: {
            "fill-color": AiLayerStyles.EVIDENCE_COLLECTIONS_COLORS.fill,
            "stroke-color": AiLayerStyles.EVIDENCE_COLLECTIONS_COLORS.stroke,
            "stroke-width": AiLayerStyles.EVIDENCE_COLLECTIONS_COLORS.strokeWidth,
            "circle-radius": 5,
            "circle-fill-color": AiLayerStyles.EVIDENCE_COLLECTIONS_COLORS.fill,
            "circle-stroke-color": AiLayerStyles.EVIDENCE_COLLECTIONS_COLORS.stroke,
          },
        },
        {
          // Zoom-gated label: the canvas style evaluator provides ["resolution"]
          // (["zoom"] is WebGL-only). Keys match what the advanced editor writes.
          filter: ["<", ["resolution"], AiLayerStyles.COLLECTIONS_LABEL_MAX_RESOLUTION],
          style: {
            "text-value": ["to-string", ["get", "collection"]],
            "text-font": "11px Roboto",
            "text-fill-color": AiLayerStyles.EVIDENCE_COLLECTIONS_COLORS.labelColor,
            "text-stroke-color": AiLayerStyles.EVIDENCE_COLLECTIONS_COLORS.labelHalo,
            "text-stroke-width": 3,
          },
        },
      ],
    },
    defaultEvidenceStyle: {
      type: "flat",
      properties: {
        "fill-color": AiLayerStyles.DEFAULT_EVIDENCE_COLORS.polygonFill,
        "stroke-color": AiLayerStyles.DEFAULT_EVIDENCE_COLORS.polygonStroke,
        "stroke-width": 2,
        "circle-radius": 5,
        "circle-fill-color": AiLayerStyles.DEFAULT_EVIDENCE_COLORS.pointFill,
      },
    },
  };

  /** OL style function for evidence layers with no type-specific style — see DEFAULT_EVIDENCE_COLORS. */
  static defaultEvidenceStyleFn(feature: any): any[] {
    const geom = feature.getGeometry?.();
    if (!geom) return [];
    const colors = AiLayerStyles.DEFAULT_EVIDENCE_COLORS;
    const type: string = geom.getType?.() ?? "";
    if (type === "Polygon" || type === "MultiPolygon") {
      return [
        new Style({
          stroke: new Stroke({ color: colors.polygonStroke, width: 2 }),
          fill: new Fill({ color: colors.polygonFill }),
        }),
      ];
    }
    if (type === "LineString" || type === "MultiLineString") {
      return [new Style({ stroke: new Stroke({ color: colors.lineStroke, width: colors.lineStrokeWidth }) })];
    }
    // Point / MultiPoint fallback
    return [
      new Style({
        image: new Circle({
          radius: 6,
          fill: new Fill({ color: colors.pointFill }),
          stroke: new Stroke({ color: "rgba(255,255,255,0.9)", width: 2 }),
        }),
      }),
    ];
  }

  /** OL style function for workarea layers — a translucent gray veil: fill-only, no stroke. */
  static workareaStyleFn(feature: any): any[] {
    const geom = feature.getGeometry?.();
    if (!geom) return [];
    const colors = AiLayerStyles.EVIDENCE_WORKAREA_COLORS;
    const type: string = geom.getType?.() ?? "";
    if (type === "Polygon" || type === "MultiPolygon") {
      // Fill-only shadow: deliberately no stroke so the workarea reads as a soft veil.
      return [new Style({ fill: new Fill({ color: colors.fill }) })];
    }
    if (type === "LineString" || type === "MultiLineString") {
      return [new Style({ stroke: new Stroke({ color: colors.lineStroke, width: colors.lineStrokeWidth }) })];
    }
    // Point / MultiPoint fallback
    return [
      new Style({
        image: new Circle({
          radius: 4,
          fill: new Fill({ color: colors.pointFill }),
        }),
      }),
    ];
  }

  /** OL style function for imagery-collection layers — thin border, soft fill and a
   *  zoom-gated `collection` label (the mandatory property every collection carries). */
  static collectionsStyleFn(feature: any, resolution: number): any[] {
    const geom = feature.getGeometry?.();
    if (!geom) return [];
    const colors = AiLayerStyles.EVIDENCE_COLLECTIONS_COLORS;
    const type: string = geom.getType?.() ?? "";
    let styles: any[];
    if (type === "Polygon" || type === "MultiPolygon") {
      styles = [
        new Style({
          stroke: new Stroke({ color: colors.stroke, width: colors.strokeWidth }),
          fill: new Fill({ color: colors.fill }),
        }),
      ];
    } else if (type === "LineString" || type === "MultiLineString") {
      styles = [new Style({ stroke: new Stroke({ color: colors.stroke, width: colors.strokeWidth }) })];
    } else {
      styles = [
        new Style({
          image: new Circle({
            radius: 5,
            fill: new Fill({ color: colors.fill }),
            stroke: new Stroke({ color: colors.stroke, width: 1 }),
          }),
        }),
      ];
    }
    // Label only when zoomed in: OL resolution shrinks as the user zooms in.
    const collectionName: string | undefined = feature.get?.("collection") ?? undefined;
    if (collectionName && resolution <= AiLayerStyles.COLLECTIONS_LABEL_MAX_RESOLUTION) {
      styles.push(
        new Style({
          text: new Text({
            text: collectionName,
            font: colors.labelFont,
            fill: new Fill({ color: colors.labelColor }),
            stroke: new Stroke({ color: colors.labelHalo, width: 3 }),
            overflow: true,
          }),
        }),
      );
    }
    return styles;
  }
}
